/** Where `named` keeps a name. Kept within this package, so a consumer can make a `Check` or `Rule` only by defining one. */
export const NAME = Symbol("name");

/**
 * A function under the name it was defined with, which a reader of a
 * decorated form sees in its place.
 *
 * The name is given rather than read from `Function.name`, which is empty for
 * an arrow handed straight to a function and the property's name for one
 * written as a property.
 */
export const named = <Args, Returns>(
  name: string,
  unnamed: (args: Args) => Returns,
): ((args: Args) => Returns) & { readonly [NAME]: string } =>
  // A fresh function, so naming never renames the one handed in.
  Object.assign((args: Args) => unnamed(args), { [NAME]: name });

/** The name `named` gave a function, or `undefined` for anything else. */
export const nameOf = (value: unknown): string | undefined => {
  const name: unknown =
    typeof value === "function" && NAME in value ? value[NAME] : undefined;

  return typeof name === "string" ? name : undefined;
};
